export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const normalizePhone = (phone) => {
  if (!phone) return "";
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");
  return digits;
};

export const isValidPhone = (phone) => {
  const normalized = normalizePhone(phone);
  return normalized.length === 10;
};

export const formatPhone = (phone) => {
  const normalized = normalizePhone(phone);
  if (normalized.length === 10) {
    return `${normalized.slice(0, 3)}-${normalized.slice(
      3,
      6
    )}-${normalized.slice(6)}`;
  }
  return phone;
};

export const isValidDate = (dateString) => {
  if (!dateString) return false;

  // Check format YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;

  // Check if valid date
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

export const isDateAfter = (dateString, afterDate) => {
  const date = new Date(dateString);
  const compareDate = new Date(afterDate);
  return date >= compareDate;
};

export const validateRequiredField = (value, fieldName) => {
  if (!value || value.toString().trim() === "") {
    return `${fieldName} is required`;
  }
  return null;
};

export const validateStudentRow = (row, rowIndex, programStartDate) => {
  const errors = [];

  // Required fields - Parent 1
  const parent1Required = [
    { field: "parent1_firstname", label: "Parent 1 First Name" },
    { field: "parent1_lastname", label: "Parent 1 Last Name" },
    { field: "parent1_email", label: "Parent 1 Email" },
    { field: "parent1_phone", label: "Parent 1 Phone" },
  ];

  parent1Required.forEach(({ field, label }) => {
    const error = validateRequiredField(row[field], label);
    if (error) errors.push({ field, message: error });
  });

  // Validate Parent 1 email format
  if (row.parent1_email && !isValidEmail(row.parent1_email)) {
    errors.push({ field: "parent1_email", message: "Invalid email format" });
  }

  // Validate Parent 1 phone format
  if (row.parent1_phone && !isValidPhone(row.parent1_phone)) {
    errors.push({ field: "parent1_phone", message: "Phone must be 10 digits" });
  }

  // Parent 2 validation - if ANY field is filled, ALL must be filled
  const parent2Fields = [
    "parent2_firstname",
    "parent2_lastname",
    "parent2_email",
    "parent2_phone",
  ];

  const parent2Filled = parent2Fields.filter(
    (field) => row[field] && row[field].toString().trim() !== ""
  );

  if (parent2Filled.length > 0 && parent2Filled.length < parent2Fields.length) {
    errors.push({
      field: "parent2",
      message: "If Parent 2 info is provided, all Parent 2 fields are required",
    });
  }

  // If all Parent 2 fields are filled, validate them
  if (parent2Filled.length === parent2Fields.length) {
    if (!isValidEmail(row.parent2_email)) {
      errors.push({ field: "parent2_email", message: "Invalid email format" });
    }
    if (!isValidPhone(row.parent2_phone)) {
      errors.push({
        field: "parent2_phone",
        message: "Phone must be 10 digits",
      });
    }
  }

  // Required fields - Student
  const studentRequired = [
    { field: "student_firstname", label: "Student First Name" },
    { field: "student_lastname", label: "Student Last Name" },
    { field: "student_dob", label: "Student Date of Birth" },
    { field: "program_start_date", label: "Program Start Date" },
  ];

  studentRequired.forEach(({ field, label }) => {
    const error = validateRequiredField(row[field], label);
    if (error) errors.push({ field, message: error });
  });

  // Validate dates
  if (row.student_dob && !isValidDate(row.student_dob)) {
    errors.push({
      field: "student_dob",
      message: "Invalid date format. Use YYYY-MM-DD",
    });
  }

  if (row.program_start_date && !isValidDate(row.program_start_date)) {
    errors.push({
      field: "program_start_date",
      message: "Invalid date format. Use YYYY-MM-DD",
    });
  }

  // Validate program start date is after program's start date
  if (
    row.program_start_date &&
    isValidDate(row.program_start_date) &&
    !isDateAfter(row.program_start_date, programStartDate)
  ) {
    errors.push({
      field: "program_start_date",
      message: `Start date must be on or after ${programStartDate}`,
    });
  }

  return errors.length > 0 ? { row: rowIndex, errors, data: row } : null;
};

export const findDuplicateStudents = (rows) => {
  const studentMap = new Map();
  const duplicates = [];

  rows.forEach((row, index) => {
    const key = `${row.student_firstname?.toLowerCase()}_${row.student_lastname?.toLowerCase()}_${
      row.student_dob
    }`;

    if (studentMap.has(key)) {
      duplicates.push({
        row: index + 1,
        duplicateOf: studentMap.get(key),
        data: row,
      });
    } else {
      studentMap.set(key, index + 1);
    }
  });

  return duplicates;
};
